import { useParams } from 'react-router-dom'
import { GenericWizard, WizardStep, useWizard } from '@/components/common/wizard/GenericWizard'
import { useQuery, useMutation } from '@apollo/client'
import { GET_AUDIENCES } from '@/graphql/audience-queries'
import { GET_AD_CAMPAIGN_BY_ID, REFRESH_PIXEL_STATUS } from '@/graphql/media-buying-queries'
import { useContext, useEffect, useState } from 'react'
import { CubeLoader } from '@/components/ui/cube-loader'
import { getAllStages, getAvailableStages } from '@/utils/ad-campaigns-helpers'
import { useMediaBuyingContext } from '@/context/media-buying-context'
import { AddAudienceIcon } from '@/components/icons/AddAudienceIcon'

// Import screen components - reuse from AdCampaignWizard
import SalesFunnelSetup from './screens/SalesFunnelSetup/Screen'
import AttachAudience from './screens/SalesFunnelSetup/AttachAudience'
import AudienceTargeting from './screens/AudienceTargeting/Screen'
import AdsSelection from './screens/AdsSelection'
import BudgetSchedule from './screens/BudgetSchedule/Screen'
import ReviewLaunch from './screens/ReviewLaunch'
import EmptyWizard from './EmptyWizard'

// Modified BudgetSchedule component for Add Audience flow
const AddAudienceBudgetSchedule = (props) => {
    return <BudgetSchedule {...props} isAddAudience={true} />
}

// Modified ReviewLaunch component for Add Audience flow
const AddAudienceReviewLaunch = (props) => {
    return <ReviewLaunch {...props} isAddAudience={true} />
}

// Component to handle pixel state within wizard context
const PixelStateManager = ({ publisher, campaignId }) => {
    const { setStepContentData, getStepContentData } = useWizard()

    // Get pixel status from connected publishers (same as CampaignObjective.jsx)
    const pixelIsValid = publisher?.pixel_active

    // Pixel refresh mutation (same as CampaignObjective.jsx)
    const [refreshPixelStatus] = useMutation(REFRESH_PIXEL_STATUS, {
        onCompleted: (data) => {
            // Update the wizard state with the refreshed pixel status
            const currentData = getStepContentData('campaignObjective') || {}
            setStepContentData(
                {
                    ...currentData,
                    pixelIsValid: data?.refreshPixelStatus,
                },
                'campaignObjective',
            )
        },
        onError: (error) => {
            console.error('Failed to refresh pixel status:', error)
        },
    })

    // Save pixel status to wizard state whenever it changes
    useEffect(() => {
        const currentData = getStepContentData('campaignObjective') || {}
        setStepContentData(
            {
                ...currentData,
                pixelIsValid: pixelIsValid,
            },
            'campaignObjective',
        )
    }, [pixelIsValid])

    // Check and refresh pixel status if it's false (same logic as CampaignObjective.jsx)
    useEffect(() => {
        if (pixelIsValid === false && publisher?.publisher_id && campaignId) {
            console.log('Pixel is inactive, refreshing status...')
            refreshPixelStatus({
                variables: {
                    publisher_id: publisher.publisher_id,
                    campaign_id: campaignId,
                },
            })
        }
    }, [pixelIsValid, publisher?.publisher_id, campaignId, refreshPixelStatus])

    return null // This component doesn't render anything
}

// Screen mappings for add audience wizard
const addAudienceScreensMap = {
    salesFunnelSetup: SalesFunnelSetup,
    attachAudience: AttachAudience,
    audienceTargeting: AudienceTargeting,
    adsSelection: AdsSelection,
    budgetSchedule: AddAudienceBudgetSchedule,
    reviewLaunch: AddAudienceReviewLaunch,
}

// Main Add Audience Wizard Component
export const AddAudienceWizard = () => {
    const { id: campaignId, ad_campaign_id } = useParams()
    const [loading, setLoading] = useState(true)
    const [initialState, setInitialState] = useState({})
    const mediaBuyingContext = useMediaBuyingContext()
    const { connectedPublishers } = mediaBuyingContext
    const publisher = connectedPublishers ? Object.values(connectedPublishers)[0] : null

    useEffect(() => {
        if (publisher?.page_id || !publisher?.pixel) mediaBuyingContext.refetch()
    }, [])

    // Fetch ad campaign data
    const { data: adCampaignData, loading: adCampaignLoading } = useQuery(GET_AD_CAMPAIGN_BY_ID, {
        variables: { id: ad_campaign_id },
        skip: !ad_campaign_id,
    })

    // Fetch audiences data
    const { data: audiencesData, loading: audiencesLoading } = useQuery(GET_AUDIENCES)

    // Clear wizard state on mount to ensure fresh start
    useEffect(() => {
        const storageKey = `addAudienceWizardState-${ad_campaign_id}`
        localStorage.removeItem(storageKey)
    }, [ad_campaign_id])

    // Update initial state when both queries complete
    useEffect(() => {
        if (!audiencesLoading && !adCampaignLoading) {
            const allAudiences = audiencesData?.Audience?.audiences || []
            const adCampaign = adCampaignData?.AdCampaign?.adCampaignById
            const initialState = calculateInitialState(allAudiences, adCampaign, publisher)

            setInitialState(initialState)
            setLoading(false)
        }
    }, [audiencesData, audiencesLoading, adCampaignData, adCampaignLoading, publisher])

    const calculateInitialState = (allAudiences, adCampaign, publisher) => {
        const allStages = getAllStages()
        const audienceSelections = {}

        // Get the objective from the fetched AdCampaign
        const objective = adCampaign?.objective || 'awareness'

        // Get available stages for this objective
        const availableStages = getAvailableStages(objective)

        // Initialize all stages with empty audiences
        allStages.map((stage) => {
            audienceSelections[stage.id] = []
        })

        // Prepare content cache with ad campaign data
        const contentCache = {
            salesFunnelSetup: { audienceSelections },
            // Pre-populate campaign objective from the ad campaign
            campaignObjective: adCampaign
                ? {
                      selectedObjective: objective,
                      publisherId: adCampaign.publisher_id,
                      // New ads follow the lead source of the campaign's existing ads
                      ...(adCampaign.lead_source ? { leadSource: adCampaign.lead_source } : {}),
                  }
                : {},
            // Set location targeting from fetched AdCampaign data
            locationTargeting: {
                selectedLocation: adCampaign?.location_targeting || 'worldwide',
                locations: [],
                locationsData: {},
            },
        }

        if (adCampaign && publisher) {
            contentCache.prefetchedData = {
                campaignName: adCampaign.name,
                publisherNameStamp: publisher.label || 'Turba Media',
                isAddAudience: true, // Flag to indicate this is add audience flow
                adCampaignId: adCampaign.id,
                campaignId: campaignId,
                campaignObjective: adCampaign.objective,
                // New audiences of a campaign with one campaign budget share it
                ...(adCampaign.budget_mode ? { campaignBudgetMode: adCampaign.budget_mode } : {}),
            }
        }

        return { contentCache }
    }

    if (!publisher || !publisher.page_id || !publisher.pixel) return <EmptyWizard />
    if (loading) return (
        <div className="tw:w-full tw:flex tw:items-center tw:justify-center">
            <CubeLoader size="default" />
        </div>
    )

    const adCampaign = adCampaignData?.AdCampaign?.adCampaignById
    if (!adCampaign) return <div className="tw:text-white tw:p-4">Ad Campaign not found</div>

    return (
        <GenericWizard
            title="New Audience"
            icon={() => <AddAudienceIcon size={22} color="#899CBD" />}
            storageKey="addAudienceWizardState"
            initialState={initialState}
            idExtractor={() => ad_campaign_id}
            screensMap={addAudienceScreensMap}
        >
            {/* Pixel state manager - handles pixel status within wizard context */}
            <PixelStateManager publisher={publisher} campaignId={campaignId} />

            {/* Skip Campaign Objective and Location Targeting steps */}
            <WizardStep name="salesFunnelSetup" label="Sales Funnel Setup" className="tw:ml-3" />
            <WizardStep name="attachAudience" label="Attach Audience" visible={false} className="tw:ml-6" />
            <WizardStep name="audienceTargeting" label="Audience Targeting" className="tw:ml-3" visible={false} />
            <WizardStep name="adsSelection" label="Ads Selection" className="tw:ml-3" />
            <WizardStep name="budgetSchedule" label="Budget & Schedule" className="tw:ml-3" />
            <WizardStep name="reviewLaunch" label="Review & Launch" className="tw:ml-3" />
        </GenericWizard>
    )
}

// Export useWizard for screen components
export { useWizard }
